import React, { useState, useEffect, useCallback } from 'react';
import { AVAILABLE_SENSORS, SensorType, SensorConfiguration, SensorValidationResult } from '../../../types/weatherStation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faArrowUp, faArrowDown, faTrash, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

/**
 * SensorManager Component
 * 
 * Manages weather station sensor configuration for two specific fields:
 * - station.sensorArray: Array of sensor tokens in transmission order
 * - station.sensorTokens: Space-separated string of selected sensor tokens
 * 
 * Provides visual interface for selecting sensors and defining their transmission order.
 * Validates sensor compatibility and enforces business rules (e.g., duplicates not allowed except 'dummy').
 */

// Props Interface
export interface SensorManagerProps {
    initialSensorTokens?: string[];
    initialSensorArray?: string[];
    onConfigurationChange: (config: SensorConfiguration) => void;
    onValidationChange?: (validation: SensorValidationResult) => void;
    disabled?: boolean;
    maxSensors?: number;
    showPreview?: boolean;
}



export const SensorManager: React.FC<SensorManagerProps> = ({
    initialSensorTokens = [],
    initialSensorArray = [],
    onConfigurationChange,
    onValidationChange,
    disabled = false,
    maxSensors = 20,
    showPreview = true
}) => {
    const [selectedSensors, setSelectedSensors] = useState<string[]>(initialSensorTokens);
    const [sensorOrder, setSensorOrder] = useState<string[]>(initialSensorArray);
    const [validation, setValidation] = useState<SensorValidationResult>({ isValid: true, errors: [], warnings: [] });

    // Group sensors by category
    const sensorsByCategory = AVAILABLE_SENSORS.reduce((acc, sensor) => {
        if (!acc[sensor.category]) {
            acc[sensor.category] = [];
        }
        acc[sensor.category].push(sensor);
        return acc;
    }, {} as Record<string, SensorType[]>);

    // Validate sensor configuration
    const validateConfiguration = useCallback((sensors: string[], order: string[]): SensorValidationResult => {
        const errors: string[] = [];
        const warnings: string[] = [];

        // Check for invalid tokens
        const validTokens = AVAILABLE_SENSORS.map(s => s.token);
        sensors.forEach(token => {
            if (!validTokens.includes(token)) {
                errors.push(`Invalid sensor token: ${token}`);
            }
        });

        // Check for duplicates (except dummy)
        const nonDummyTokens = sensors.filter(token => token !== 'dummy');
        const duplicates = nonDummyTokens.filter((token, index) => nonDummyTokens.indexOf(token) !== index);
        if (duplicates.length > 0) {
            errors.push(`Duplicate sensors not allowed (except dummy): ${[...new Set(duplicates)].join(', ')}`);
        }

        // Check max sensors limit
        if (sensors.length > maxSensors) {
            errors.push(`Too many sensors selected (${sensors.length}/${maxSensors})`);
        }

        // Check order consistency
        const missingFromOrder = sensors.filter(token => !order.includes(token));
        const extraInOrder = order.filter(token => !sensors.includes(token));

        if (missingFromOrder.length > 0) {
            warnings.push(`Sensors not in transmission order: ${missingFromOrder.join(', ')}`);
        }

        if (extraInOrder.length > 0) {
            warnings.push(`Extra sensors in order list: ${extraInOrder.join(', ')}`);
        }

        // Performance warnings
        if (sensors.length > 30) {
            warnings.push('Large number of sensors may impact transmission performance');
        }

        return {
            isValid: errors.length === 0,
            errors,
            warnings
        };
    }, [maxSensors]);

    // Update configuration when sensors or order changes
    useEffect(() => {
        const newValidation = validateConfiguration(selectedSensors, sensorOrder);
        setValidation(newValidation);

        const config: SensorConfiguration = {
            selectedSensors,                          // Internal working array
            sensorTokens: selectedSensors,           // → Will be converted to space-separated string for station.sensorTokens
            sensorArray: sensorOrder,                // → Direct mapping to station.sensorArray (transmission order)
            tokenString: selectedSensors.join(',')   // Preview string (comma-separated)
        };

        onConfigurationChange(config);
        if (onValidationChange) {
            onValidationChange(newValidation);
        }
    }, [selectedSensors, sensorOrder, onConfigurationChange, onValidationChange, validateConfiguration]);

    // Toggle sensor selection
    const toggleSensor = useCallback((token: string) => {
        if (disabled) return;

        setSelectedSensors(prev => {
            if (token === 'dummy') {
                // Always allow adding another dummy, or remove one dummy at a time
                if (prev.length >= maxSensors) {
                    return prev; // Don't exceed max
                }
                return [...prev, token];
            }
            if (prev.includes(token)) {
                // Remove sensor
                const newSelected = prev.filter(t => t !== token);
                setSensorOrder(prevOrder => prevOrder.filter(t => t !== token));
                return newSelected;
            } else {
                // Add sensor (no duplicates except dummy)
                if (prev.includes(token)) {
                    return prev; // Don't add duplicates
                }
                if (prev.length >= maxSensors) {
                    return prev; // Don't exceed max
                }
                const newSelected = [...prev, token];
                setSensorOrder(prevOrder => [...prevOrder, token]);
                return newSelected;
            }
        });
        // For dummy, always add another; for others, toggle add/remove
        if (token === 'dummy') {
            setSensorOrder(prevOrder => [...prevOrder, token]);
        }
    }, [disabled, maxSensors]);

    // Move sensor in order
    const moveSensor = useCallback((fromIndex: number, toIndex: number) => {
        if (disabled) return;

        setSensorOrder(prev => {
            const newOrder = [...prev];
            const [moved] = newOrder.splice(fromIndex, 1);
            newOrder.splice(toIndex, 0, moved);
            return newOrder;
        });
    }, [disabled]);

    // Remove sensor from order
    const removeSensorFromOrder = useCallback((token: string) => {
        if (disabled) return;

        setSelectedSensors(prev => prev.filter(t => t !== token));
        setSensorOrder(prev => prev.filter(t => t !== token));
    }, [disabled]);

    // Get sensor info by token
    const getSensorInfo = (token: string): SensorType | null => {
        return AVAILABLE_SENSORS.find(s => s.token === token) || null;
    };

    // Check if sensor is selected
    const isSensorSelected = (token: string): boolean => {
        return selectedSensors.includes(token);
    };

    // Check if sensor can be selected
    const canSelectSensor = (token: string): boolean => {
        if (disabled) return false;
        if (selectedSensors.length >= maxSensors) return false;
        if (token !== 'dummy' && selectedSensors.includes(token)) return false;
        return true;
    };

    return (
        <div className="sensor-manager-container">
            {/* Header */}
            <div className="sensor-manager-header">
                <h3 className="sensor-manager-title">Sensor Configuration</h3>
                <span className="sensor-manager-counter">
                    {selectedSensors.length} / {maxSensors}
                </span>
            </div>

            {/* Available Sensors by Category */}
            {Object.entries(sensorsByCategory).map(([category, sensors]) => (
                <div key={category} className="sensor-manager-section">
                    <div className="sensor-manager-section-title">
                        {category.charAt(0).toUpperCase() + category.slice(1)} Sensors
                    </div>
                    <div className="sensor-manager-sensor-grid">
                        {sensors.map(sensor => {
                            const isSelected = isSensorSelected(sensor.token);
                            const canSelect = canSelectSensor(sensor.token);

                            return (
                                <button
                                    key={sensor.token}
                                    className={[
                                        'sensor-manager-sensor-button',
                                        isSelected ? 'sensor-manager-sensor-button-active' : '',
                                        !canSelect && !isSelected ? 'sensor-manager-sensor-button-disabled' : ''
                                    ].filter(Boolean).join(' ')}
                                    type="button"
                                    onClick={() => toggleSensor(sensor.token)}
                                    disabled={!canSelect && !isSelected}
                                    title={`${sensor.description}${sensor.unit ? ` (${sensor.unit})` : ''}`}
                                >
                                    <span>
                                        <strong>{sensor.token}</strong><br />
                                        <small>{sensor.name}</small>
                                    </span>
                                    <FontAwesomeIcon icon={isSelected ? faMinus : faPlus} />
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* Selected Sensors Order */}
            <div className="sensor-manager-section">
                <div className="sensor-manager-section-title">
                    Transmission Order ({sensorOrder.length} sensors)
                </div>
                <div className="sensor-manager-selected-sensors">
                    {sensorOrder.length === 0 ? (
                        <div className="sensor-manager-empty-state">
                            No sensors selected. Choose sensors from the categories above.
                        </div>
                    ) : (
                        sensorOrder.map((token, index) => {
                            const sensorInfo = getSensorInfo(token);
                            return (
                                <div key={`${token}-${index}`} className="sensor-manager-selected-sensor-item">
                                    <div className="sensor-manager-sensor-info">
                                        <span className="sensor-manager-sensor-index">
                                            {index + 1}.
                                        </span>
                                        <span className="sensor-manager-sensor-token">{token}</span>
                                        <span className="sensor-manager-sensor-name">
                                            {sensorInfo?.name || 'Unknown Sensor'}
                                        </span>
                                    </div>
                                    <div className="sensor-manager-sensor-actions">
                                        <button
                                            className="sensor-manager-action-button"
                                            type="button"
                                            onClick={() => moveSensor(index, Math.max(0, index - 1))}
                                            disabled={disabled || index === 0}
                                            title="Move up"
                                        >
                                            <FontAwesomeIcon icon={faArrowUp} />
                                        </button>
                                        <button
                                            className="sensor-manager-action-button"
                                            type="button"
                                            onClick={() => moveSensor(index, Math.min(sensorOrder.length - 1, index + 1))}
                                            disabled={disabled || index === sensorOrder.length - 1}
                                            title="Move down"
                                        >
                                            <FontAwesomeIcon icon={faArrowDown} />
                                        </button>
                                        <button
                                            className="sensor-manager-action-button"
                                            type="button"
                                            onClick={() => removeSensorFromOrder(token)}
                                            disabled={disabled}
                                            title="Remove sensor"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Token String Preview */}
            {showPreview && (
                <div className="sensor-manager-section">
                    <div className="sensor-manager-section-title">Token String Preview</div>
                    <div className="sensor-manager-token-string-preview">
                        {selectedSensors.length > 0 ? selectedSensors.join(',') : 'No sensors selected'}
                    </div>
                </div>
            )}

            {/* Validation Messages */}
            {validation.errors.length > 0 && (
                <div className="sensor-manager-validation-message sensor-manager-validation-error">
                    <strong>Errors:</strong>
                    <ul className="sensor-manager-validation-list">
                        {validation.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}

            {validation.warnings.length > 0 && (
                <div className="sensor-manager-validation-message sensor-manager-validation-warning">
                    <strong>Warnings:</strong>
                    <ul className="sensor-manager-validation-list">
                        {validation.warnings.map((warning, index) => (
                            <li key={index}>{warning}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SensorManager;